/**
 * Canonical CimmplePay pricing & positioning (US).
 * Import from here instead of hardcoding amounts in page components.
 *
 * Positioning: Payroll Management & HR platform.
 * CimmplePay calculates payroll and prepares it for payment;
 * employee payments are completed via the customer's bank/payment provider.
 */

export interface CimmplePayPlan {
  id: string;
  name: string;
  price: string;
  period: string;
  /** Displayed under the base price, e.g. "+ $2 per employee/month" */
  perEmployee: string;
  description: string;
  features: string[];
  highlighted: boolean;
  badge?: string;
  cta: string;
  /** Opens signup modal vs links to contact for demos */
  ctaAction: 'trial' | 'demo';
}

export interface CimmplePayFaq {
  q: string;
  a: string;
}

export interface CimmplePayComparisonFeature {
  name: string;
  starter: boolean;
  growth: boolean;
  pro: boolean;
}

export interface CimmplePayComparisonCategory {
  name: string;
  features: CimmplePayComparisonFeature[];
}

export const cimmplePayHero = {
  title: 'Payroll management & HR.',
  titleAccent: 'Simple, transparent pricing.',
  subtitle:
    'Base platform fee plus a clear per-employee rate. Calculate payroll, manage employees, and prepare payroll for payment.',
  primaryCta: 'Start Free Trial',
  secondaryCta: 'Book a Demo',
} as const;

export const cimmplePayPayrollYourWay = {
  eyebrow: 'Flexibility',
  title: 'Payroll Your Way',
  paragraphs: [
    'CimmplePay helps you calculate payroll, generate pay stubs, manage employees, and prepare payroll for payment.',
    'After payroll is finalized, you simply use your preferred bank or payment provider to pay employees.',
    'This gives you the flexibility to keep your existing banking relationships while modernizing payroll and HR management.',
  ],
} as const;

/** Single source of truth for CimmplePay SaaS pricing plans. */
export const cimmplePayPlans: CimmplePayPlan[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$19',
    period: '/month',
    perEmployee: '+ $2 per employee/month',
    description: 'Ideal for small businesses getting started.',
    features: [
      'Payroll calculations',
      'Unlimited payroll runs',
      'Payroll reports',
      'Payroll tax calculations',
      'Pay stub generation',
      'Payroll exports',
      'Employee self-service',
      'Employee records',
      'Email support',
    ],
    highlighted: false,
    cta: 'Start Free Trial',
    ctaAction: 'trial',
  },
  {
    id: 'growth',
    name: 'Growth',
    price: '$35',
    period: '/month',
    perEmployee: '+ $3 per employee/month',
    description: 'Everything in Starter plus workforce & HR tools.',
    features: [
      'Everything in Starter',
      'Time & Attendance',
      'Leave Management',
      'PTO tracking',
      'Manager approvals',
      'HR workflows',
      'Employee document management',
      'Multi-location support',
      'Advanced payroll reports',
      'Standard integrations',
    ],
    highlighted: true,
    badge: 'Most Popular',
    cta: 'Start Free Trial',
    ctaAction: 'trial',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$59',
    period: '/month',
    perEmployee: '+ $4 per employee/month',
    description: 'Everything in Growth plus advanced controls.',
    features: [
      'Everything in Growth',
      'Advanced HR management',
      'Compliance dashboards',
      'API access',
      'Advanced analytics',
      'Custom approval workflows',
      'Custom roles & permissions',
      'Priority support',
    ],
    highlighted: false,
    cta: 'Book a Demo',
    ctaAction: 'demo',
  },
];

export const cimmplePayStartingPrice = 'From $19/month';

export const cimmplePayComparison: CimmplePayComparisonCategory[] = [
  {
    name: 'Payroll Management',
    features: [
      { name: 'Payroll calculations', starter: true, growth: true, pro: true },
      { name: 'Unlimited payroll runs', starter: true, growth: true, pro: true },
      { name: 'Payroll tax calculations', starter: true, growth: true, pro: true },
      { name: 'Payroll exports', starter: true, growth: true, pro: true },
      { name: 'Pay stub generation', starter: true, growth: true, pro: true },
    ],
  },
  {
    name: 'Workforce Management',
    features: [
      { name: 'Employee portal', starter: true, growth: true, pro: true },
      { name: 'Employee records', starter: true, growth: true, pro: true },
      { name: 'Time & Attendance', starter: false, growth: true, pro: true },
      { name: 'Leave Management', starter: false, growth: true, pro: true },
      { name: 'PTO tracking', starter: false, growth: true, pro: true },
    ],
  },
  {
    name: 'HR & Compliance',
    features: [
      { name: 'HR workflows', starter: false, growth: true, pro: true },
      { name: 'Compliance dashboards', starter: false, growth: false, pro: true },
      { name: 'Advanced reporting', starter: false, growth: true, pro: true },
      { name: 'API access', starter: false, growth: false, pro: true },
    ],
  },
  {
    name: 'Support',
    features: [
      { name: 'Email support', starter: true, growth: true, pro: true },
      { name: 'Priority support', starter: false, growth: false, pro: true },
    ],
  },
];

export const cimmplePayFaqs: CimmplePayFaq[] = [
  {
    q: 'Does CimmplePay send employee payments?',
    a: 'No. CimmplePay calculates payroll, generates pay stubs, and prepares payroll for payment. Employee payments are completed through your preferred bank or payment provider.',
  },
  {
    q: 'Does CimmplePay calculate payroll taxes?',
    a: 'Yes. CimmplePay calculates payroll taxes and generates payroll reports.',
  },
  {
    q: 'Can I import my employees?',
    a: 'Yes. You can import employee records to get started quickly and keep workforce data up to date.',
  },
  {
    q: 'Can I run unlimited payrolls?',
    a: 'Yes. All plans include unlimited payroll runs.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes. Start a free trial to explore payroll management, HR tools, and employee self-service—no long-term commitment required to try it.',
  },
  {
    q: 'What is included in Growth vs Starter?',
    a: 'Growth includes everything in Starter, plus Time & Attendance, Leave Management, PTO tracking, manager approvals, HR workflows, document management, multi-location support, advanced payroll reports, and standard integrations.',
  },
  {
    q: 'When should I choose Pro?',
    a: 'Pro is best when you need advanced HR management, compliance dashboards, API access, advanced analytics, custom approval workflows, custom roles & permissions, and priority support.',
  },
];

/** Homepage teaser highlights for CimmplePay. */
export const cimmplePayTeaserFeatures = [
  'Payroll calculations & tax estimates',
  'Pay stubs & payroll exports',
  'Employee records & self-service',
  'Time, leave & PTO (Growth+)',
  'HR workflows & reporting',
  'Transparent per-employee pricing',
] as const;
